import {defineStore} from 'pinia'
import {NetworkService} from "~/utils/NetworkService";
import { useGlobalEvent, useNotificationEvent} from "~/composables/useEventBus";
import {type NotificationDetails} from "~/models/notificationDetails";
import {NotificationType} from "~/utils/enums/NotificationType";
import type {AddAnnouncementRb, AddUpdateAnnouncementRb} from "~/models/request/announcements/addUpdateAnnouncementRb";
import type {GetAnnouncementBySenderRb} from "~/models/request/announcements/getAnnouncementBySenderRb";
import type {GetAnnouncementByAnnouncementIdRb} from "~/models/request/announcements/getAnnouncementByAnnouncementIdRb";
import type {GetAnnouncementByCompanyRb} from "~/models/request/announcements/getAnnouncementByCompanyRb";
import type {
    DeleteAnnouncementByAnnouncementIdRb
} from "~/models/request/announcements/deleteAnnouncementByAnnouncementId";

export interface CreateAnnouncementResult {
    ok: boolean
    data?: any
    error?: string
    insufficientBalance?: { required: number, available: number }
}

export const announcementStore = defineStore({
    id: 'announcement-store',
    persist: true,
    state: () => {
        return {}
    },
    actions: {
        // Recipients (parent accounts) and open programs with their recipient ids, in one call
        getAnnouncementRecipients: async function (companyId: number) {
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/TextMessage/getAnnouncementRecipients';
            const ns = new NetworkService();
            let {Result, IsOperationSuccessful, ErrorMessages} = await ns.post$fetch(url, {companyId}, null, "getAnnouncementRecipients" + new Date().getMilliseconds() + "_")
            if (!IsOperationSuccessful) throw new Error(ErrorMessages || 'Could not load the recipients.');
            return Result as { recipients: any[], programs: any[] };
        },
        /**
         * Creates the announcement and says plainly whether it worked.
         *
         * It used to return the server's ErrorMessages string on failure, which
         * the dialog took for a saved announcement and reported as published.
         * Success now means the server returned the new thread's id.
         */
        createAnnouncement: async function (addAnnouncementPayload: AddAnnouncementRb): Promise<CreateAnnouncementResult> {
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/TextMessage/createText';
            const ns = new NetworkService();
            try {
                const Response = await ns.post$fetch(url, addAnnouncementPayload, null, "addAnnouncementIntoServer" + new Date().getMilliseconds() + "_")
                if (Response?.IsResult && Response.Result?.id) {
                    return {ok: true, data: Response.Result};
                }
                const serverError = Response?.ErrorMessages;
                if (serverError && typeof serverError === 'object' && serverError.message === 'Insufficient balance') {
                    return {
                        ok: false,
                        error: 'Not enough text message credits to send this announcement as a text.',
                        insufficientBalance: {
                            required: Number(serverError.currentTextTotalWeight) || 0,
                            available: Number(serverError.availableBalance) || 0
                        }
                    };
                }
                const message = typeof serverError === 'string' ? serverError : serverError?.message;
                return {ok: false, error: message || 'The announcement could not be saved. Please try again.'};
            } catch (error) {
                return {ok: false, error: 'Could not reach the server. Please check your connection and try again.'};
            }
        },
        getAnnouncementsBySenderId: async function (getAnnouncementsRb: GetAnnouncementBySenderRb) {
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/TextMessage/GetAllThreadsByUserId';
            const ns = new NetworkService();
            let {Result} = await ns.post$fetch(url, getAnnouncementsRb, null, "getAnnouncementsList" + new Date().getMilliseconds() + "_")
            if(Result?.length > 0){
                return Result;
            }
        },
        getAnnouncementByAnnouncementId: async function (getAnnouncementRb: GetAnnouncementByAnnouncementIdRb) {
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/TextMessage/getThreadDetails';
            const ns = new NetworkService();
            let {Result} = await ns.post$fetch(url, getAnnouncementRb, null, "getAnnouncementDetails" + new Date().getMilliseconds() + "_")
            if(Result){
                return Result;
            } else {
                useGlobalEvent(useNotificationEvent(), <NotificationDetails>{
                    type: NotificationType.ERROR,
                    title: 'Announcement not found issue',
                    message: 'Announcement is not found.',
                })
            }
        },
        getAnnouncementByCompanyId: async function (getAnnouncementRb: GetAnnouncementByCompanyRb){
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/TextMessage/GetAllThreadsByCompanyId';
            const ns = new NetworkService();
            try {
                let {Result} = await ns.post$fetch(url, getAnnouncementRb, null, "getAnnouncementsByCompanyId" + new Date().getMilliseconds() + "_")
                if(Result){
                    return Result;
                } else {
                    useGlobalEvent(useNotificationEvent(), <NotificationDetails>{
                        type: NotificationType.ERROR,
                        title: 'Announcement Issue',
                        message: 'Announcement is not found.',
                    })
                }
            } catch(err) {
                console.log("Error from getAnnouncementByCompanyId: " + err)
            }
        },
        updateAnnouncementByAnnouncementId: async function (updateAnnouncementRb: AddUpdateAnnouncementRb) {
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/TextMessage/updateConversationThread';
            const ns = new NetworkService();
            try {
                const Response = await ns.post$fetch(url, updateAnnouncementRb, null, "updateAnnouncementInServer" + new Date().getMilliseconds() + "_")
                return Response?.IsResult && Response.Result ? Response.Result : null;
            } catch (error) {
                return null;
            }
        },
        deleteAnnouncementByAnnouncementId: async function (deleteAnnouncementRb: DeleteAnnouncementByAnnouncementIdRb) {
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/TextMessage/deleteConversationThread';
            const ns = new NetworkService();
            try {
                const Response = await ns.post$fetch(url, deleteAnnouncementRb, null, "deleteAnnouncementInServer" + new Date().getMilliseconds() + "_")
                return Response?.IsResult && Response.Result ? Response.Result : null;
            } catch (error) {
                return null;
            }
        },
    },
    getters: {
    }
})
